import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { repository } from '../../data'
import { Panel, Section } from '../primitives'
import { ago, problemOf } from './format'

/**
 * Everything on the network that is not a grant here: the office networks,
 * the droplet's route, the groups. Kronos carries it unchanged and appends its
 * own rules. Saving pushes first and keeps the text only if the network server
 * accepts it, so a typo here never strands access changes behind it.
 *
 * Closed by default: it is rarely touched, and it is the one place on this page
 * where a mistake affects machines Kronos does not manage.
 */
export function OtherRules({ base }: { base: { savedAt: string; savedByName: string } | null }): JSX.Element {
  const [open, setOpen] = useState(false)
  return (
    <Section label="Other network rules">
      <Panel padded>
        <div className="access-status">
          <span className="muted">
            Office networks, routes and groups, carried unchanged.
            {base ? ` Last changed by ${base.savedByName} ${ago(base.savedAt)}.` : ''}
          </span>
          <button onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? 'Close' : 'View and edit'}
          </button>
        </div>
        {open ? <Editor onSaved={() => setOpen(false)} /> : null}
      </Panel>
    </Section>
  )
}

function Editor({ onSaved }: { onSaved: () => void }): JSX.Element {
  const queryClient = useQueryClient()
  const current = useQuery({ queryKey: ['access-base'], queryFn: () => repository.baseRules() })
  const [draft, setDraft] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [problem, setProblem] = useState('')

  const save = useMutation({
    mutationFn: () => repository.saveBaseRules(draft ?? '', note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectivity'] })
      void queryClient.invalidateQueries({ queryKey: ['access-base'] })
      onSaved()
    },
    onError: (e: Error) => setProblem(problemOf(e)),
  })

  if (current.isPending) return <p className="muted">Loading…</p>
  if (current.isError) return <p className="critical">Could not load the rules.</p>

  const text = draft ?? current.data.policy
  const changed = draft !== null && draft !== current.data.policy
  return (
    <div className="stack" style={{ marginTop: 12 }}>
      <textarea
        className="access-rules"
        spellCheck={false}
        value={text}
        rows={18}
        onChange={(e) => setDraft(e.currentTarget.value)}
      />
      <label className="stack" style={{ gap: 4 }}>
        <span className="field-label">What changed</span>
        <input value={note} placeholder="e.g. new office network" onChange={(e) => setNote(e.currentTarget.value)} />
      </label>
      {problem ? <p className="critical" role="alert">{problem}</p> : null}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="primary" disabled={!changed || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Checking with the network server…' : 'Save and push'}
        </button>
      </div>
    </div>
  )
}
